import React, { useEffect, useState } from "react";
import { useFormData } from "../../context/FormDataContext";
import { Box, Paper, FormControlLabel, Switch } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import ChatMessageList from "./ChatMessageList";
import ChatInput from "./ChatInput";
import { useChatHistory } from "./useChatHistory";
import { useContextText } from "./useContextText";
import { useLocaleNavigation } from "./useLocaleNavigation";
import { useImageReference } from "./useImageReference";
import { useImageGeneration } from "./useImageGeneration";
import { convertBlobToFile } from "./imageUtils";
import { deleteChatHistory } from "./historyDB";

interface GeneratorPickerProps {
  fieldId: string;
  onFileChanged: (file: File) => void;
  currentImageUrl?: string;
}

const GeneratorPicker: React.FC<GeneratorPickerProps> = ({ fieldId, onFileChanged, currentImageUrl }) => {
  const { t } = useTranslation();
  const { formData } = useFormData();
  const [inputText, setInputText] = useState<string>("");
  const exerciseId = formData?.id || "";
  const translations = React.useMemo(() => formData?.levels?.[0]?.translations || [], [formData]);

  // Custom hooks for state management
  const { messages, setMessages } = useChatHistory(`${exerciseId}_${fieldId}`);
  const { localeIndex, handlePreviousLocale, handleNextLocale } = useLocaleNavigation(translations);
  const contextText = useContextText({ formData, translations, localeIndex });
  const { selectedChatImageForReference, handleUseAsReference, clearReference } = useImageReference(messages);
  const [useExerciseContext, setUseExerciseContext] = useState<boolean>(true);
  const { isGenerating, handleSendMessage: sendMessage } = useImageGeneration({
    setMessages,
    disableContext: !useExerciseContext,
  });

  // Pre-fill input with context text if no messages yet, otherwise clear it
  useEffect(() => {
    if (messages.length === 0 && contextText) {
      setInputText(contextText);
    } else if (messages.length > 0) {
      setInputText("");
    }
  }, [contextText, messages.length]);

  // Wrapper for handleSendMessage that clears input only on success
  const handleSendMessage = async (prompt: string, referenceImage: string | null) => {
    const success = await sendMessage(prompt, referenceImage);
    if (success) {
      setInputText("");
    }
  };

  const handleUseImage = (messageId: string) => {
    const message = messages.find(msg => msg.id === messageId);
    if (!message?.imageBlob) {
      console.error("Image blob not found");
      return;
    }

    try {
      const file = convertBlobToFile(message.imageBlob, `${t("imageGenerator.generatedImageFilename")}.jpg`);
      onFileChanged(file);
    } catch (error) {
      console.error("Error converting blob to file:", error);
    }
  };

  const clearChatHistory = async () => {
    // Revoke blob URLs to free memory
    messages.forEach(msg => {
      if (msg.imageUrl) {
        URL.revokeObjectURL(msg.imageUrl);
      }
    });

    // Clear from IndexedDB and state
    await deleteChatHistory(`${exerciseId}_${fieldId}`);
    setMessages([]);
    setInputText(contextText);

    // Clear the reference image and hide the chat image tab
    clearReference();
  };

  const handleClearHistory = async () => {
    if (window.confirm(t("imageGenerator.confirmClearHistory"))) {
      await clearChatHistory();
    }
  };

  return (
    <Paper elevation={2} style={{ border: "2px solid #999", marginTop: 18, padding: 0 }}>
      <Box>
        <ChatMessageList
          messages={messages}
          onUseImage={handleUseImage}
          onUseAsReference={handleUseAsReference}
          onClearHistory={handleClearHistory}
          disabled={isGenerating}
        />

        <Paper variant="outlined">
          <Box p={2}>
            <Box mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useExerciseContext}
                    onChange={e => setUseExerciseContext(e.target.checked)}
                    color="primary"
                    disabled={isGenerating}
                  />
                }
                label={t("imageGenerator.useExerciseContext")}
              />
            </Box>
            <ChatInput
              value={inputText}
              onChange={setInputText}
              onSend={handleSendMessage}
              disabled={isGenerating}
              chatImageUrl={selectedChatImageForReference?.displayUrl}
              chatImageDataUrl={selectedChatImageForReference?.dataUrl}
              currentExerciseImageUrl={currentImageUrl}
              isFirstMessage={messages.length === 0}
              localeIndex={localeIndex}
              translations={translations}
              onPreviousLocale={handlePreviousLocale}
              onNextLocale={handleNextLocale}
            />
          </Box>
        </Paper>
      </Box>
    </Paper>
  );
};

export default GeneratorPicker;
